import { inject, Service } from '@angular/core';
import { Location } from '@angular/common';
import { Language } from '@api/models';
import { LanguageService } from './language/language.service';
import { WEBSITE_ORIGIN } from '../constants';

/** Whether a query-param change is a new destination or a refinement of the current one. */
export type HistoryChange = 'push' | 'replace';

@Service()
export class UrlService {
  private location = inject(Location);
  private languageService = inject(LanguageService);


  getQueryParams(): URLSearchParams {
    const [, search] = this.location.path(false).split('?');
    return new URLSearchParams(search);
  }

  getQueryParam(param: string): string | null {
    const params = this.getQueryParams();
    return params.get(param);
  }

  setQueryParam(param: string, value: string | number | boolean, historyChange: HistoryChange = 'push') {
    const [ pathname, search ] = this.location.path(false).split('?');

    const params = new URLSearchParams(search);
    params.set(param, String(value));

    this.applyToHistory(pathname, params, historyChange);
  }

  deleteQueryParams(paramsToDelete: string[], historyChange: HistoryChange = 'push') {
    const [ pathname, search ] = this.location.path(false).split('?');
    const params = new URLSearchParams(search);

    paramsToDelete.forEach(param => params.delete(param));
    this.applyToHistory(pathname, params, historyChange);
  }

  private applyToHistory(pathname: string, params: URLSearchParams, historyChange: HistoryChange): void {
    // Carry the existing state over: it holds the router's navigationId and the key
    // ScrollRestorationService files this history entry's position under.
    const state = this.location.getState();

    if (historyChange === 'push') {
      this.location.go(pathname, params.toString(), state);
    } else {
      this.location.replaceState(pathname, params.toString(), state);
    }
  }

  buildCurrentUrlWithLang(lang: Language, withQueryParams: boolean): string {
    const path = this.buildCurrentPathWithLang(lang, withQueryParams);
    return `${WEBSITE_ORIGIN}${path}`;
  }

  buildCurrentPathWithLang(lang: Language, withQueryParams: boolean): string {
    let path = this.location.path(true);
    if (!withQueryParams) {
      path = path.split('?')[0];
    }

    // remove current lang from path
    const currentRouteLang = this.languageService.getCurrentRouteLang();
    if (currentRouteLang) {
      const indexOfLangEnd = path.indexOf(currentRouteLang) + currentRouteLang.length;
      path = path.slice(indexOfLangEnd);
    }

    // prepend needed lang
    const routeLang = this.languageService.getRouteLangFromLang(lang);
    if (routeLang) {
      path = `/${routeLang}${path}`;
    }

    return path;
  }
}
